<template>
  <div :class="$style.container" data-testid="price-trends-hg">
    <HgCardFlat>
      <div v-if="isLoading" :class="$style.loadingWrapper" data-testid="price-trends-skeleton">
        <div :class="$style.headingWrapper">
          <HgSkeleton height="30px" width="220px" />
          <HgSkeleton height="50px" width="116px" />
        </div>
        <HgSkeleton height="180px" />
      </div>
      <template v-else>
        <div :class="[$style.headingWrapper, { [$style.hasModal]: hasModal }]">
          <div>
            <h3 :class="$style.heading">
              {{ $t('localLanding.priceTrends.title') }}
              <HgIcon
                v-if="hasModal"
                icon="Info"
                color="black"
                :class="$style.infoIcon"
                data-testid="info-icon"
                @click="emit('onInfoIconClick')"
              />
            </h3>
            <p v-if="$slots.subtitle" :class="$style.subtitle"><slot name="subtitle" /></p>
          </div>
          <HgSelect
            v-model="selectedRange"
            :disabled="!isMounted || !series[0]?.data?.length"
            :options="selections"
            name="yearRangeSelector"
            :class="$style.selectFilter"
            @update:modelValue="handleUpdatedRangeHg"
          />
        </div>
        <NoDataView v-if="!series[0]?.data?.length" :class="$style.noDataView" />
        <ApexChart
          v-if="isMounted"
          :key="locale"
          type="bar"
          :options="chartOptions"
          :series="series"
          :class="$style.chart"
          :height="chartHeight"
        />
        <HgSkeleton v-else height="200px" />
      </template>
    </HgCardFlat>
  </div>
</template>

<script setup lang="ts">
import {
  HgCardFlat,
  HgIcon,
  HgSelect,
  HgSkeleton,
  HgSupportedLanguage,
  hgNumber,
} from '@smg-real-estate/domus';
import { ApexOptions } from 'apexcharts';
import { computed, defineAsyncComponent, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import NoDataView from '@/components/property-details-page/NoDataView/NoDataView.vue';
import { PriceEvolutionResponse } from '@/models/queries/getPriceEvolution';
import { MAX_TICK_AMOUNT, PriceTrendsRangeSummary, PropertyType } from '@/types';
import { HgSegmentedOptions, HgSelectOptions } from '@smg-real-estate/domus/index';
import { usePriceTrends } from './usePriceTrends';

const ApexChart = defineAsyncComponent(() => import('vue3-apexcharts'));

interface Props {
  priceTrendsData: PriceEvolutionResponse | null;
  propertyType: PropertyType;
  isLoading: boolean;
  hasModal?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  hasModal: false,
});

// Define emits
const emit = defineEmits<{
  onInfoIconClick: [];
  onRangeChange: [value: PriceTrendsRangeSummary, isInitial: boolean];
}>();

const { locale } = useI18n();

// Component-specific constants
const selectedRange = ref('0');
const chartHeight = 205;
const colors = {
  //matches color token from Domus
  blue_60: '#1e50ad',
  teal_140: '#00627a',
};

const priceTrendsDataRef = computed(() => props.priceTrendsData);
const propertyTypeRef = computed(() => props.propertyType);

// Use the composable
const {
  isMounted,
  series,
  categories,
  min,
  max,
  selections,
  getXTextByIndex,
  handleUpdatedRangeHg,
  handleRangeChangeHg,
} = usePriceTrends({
  selectedRange,
  priceTrendsData: priceTrendsDataRef,
  propertyType: propertyTypeRef,
  onRangeChange: (value, isInitial) => emit('onRangeChange', value, isInitial),
});

// Chart options
const chartOptions = computed((): ApexOptions => {
  return {
    chart: {
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      colors: [colors.blue_60],
    },
    stroke: {
      width: 2,
      colors: ['transparent'],
    },
    plotOptions: {
      bar: {
        columnWidth: '8px',
        borderRadius: 3,
      },
    },
    markers: {
      hover: {
        size: 0,
      },
    },
    xaxis: {
      type: 'category',
      overwriteCategories: categories.value,
      tooltip: {
        enabled: false,
      },
      labels: {
        offsetX: -2,
      },
      crosshairs: {
        width: 1,
        stroke: {
          width: 1,
          dashArray: 0,
        },
        fill: {
          type: 'solid',
          color: colors.teal_140,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val: number) => hgNumber(val, locale.value as HgSupportedLanguage),
      },
      min: min.value,
      max: max.value,
      tickAmount: MAX_TICK_AMOUNT,
    },
    tooltip: {
      custom: ({ dataPointIndex, seriesIndex, series }) => {
        const formattedXValue = getXTextByIndex(dataPointIndex);
        const yValue = hgNumber(
          series[seriesIndex][dataPointIndex],
          locale.value as HgSupportedLanguage,
        );
        return `<div class="tooltip-wrapper">
                  <p class="tooltip-content-x">${formattedXValue}</p>
                  <p class="tooltip-content-y">${yValue}CHF</p>
                </div>`;
      },
    },
  };
});

watch(
  selections,
  (value: HgSelectOptions[] | HgSegmentedOptions[]) => {
    const range = value?.[0]?.value ?? 0;
    selectedRange.value = range.toString();
    handleRangeChangeHg(+range, true);
  },
  { immediate: true },
);
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$filter-width: 116px;

.container {
  height: 100%;
  padding: 0;
  color: $color-grey-100;

  [class*='HgCardFlat_cardFlat'] {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: inherit;
    padding: $space-150;
    border: unset;
    box-shadow: $elevation-minimal;
  }
}

.noDataView {
  position: absolute;
  top: 50%;
  left: 30%;
  z-index: 1;
}

.loadingWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-75;
  justify-content: space-between;
  width: 100%;
  height: 100%;
}

.card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: inherit;
  padding: $space-150;
  border: unset;
  box-shadow: $elevation-minimal;
}

.headingWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-150;

  @include grid-media-query(md) {
    flex-direction: row;
    gap: unset;
    justify-content: space-between;
  }

  &.hasModal {
    @include grid-media-query(md) {
      flex-direction: column;
      gap: $space-150;
      justify-content: unset;
    }

    .selectFilter {
      @include grid-media-query(md) {
        width: $filter-width;
      }
    }
  }
}

.selectFilter {
  width: $filter-width;
  padding-top: 0 !important; // to overwrite the original padding top of HgSelect

  @include grid-media-query(md) {
    width: auto;
  }
}

.heading {
  @include heading-3;
  @include vertical-margin;
}

.infoIcon {
  margin-left: $space-25;
  vertical-align: calc(-1 * $space-25 / 2);
  cursor: pointer;
}

.subtitle {
  @include vertical-margin;
  @include body-3;
}

.chart {
  width: calc(100% + $space-50);
  margin-left: -$space-50;
  margin-bottom: -$space-100;

  [class*='apexcharts-tooltip'] {
    &[class*='apexcharts-theme-light'] {
      top: 20px !important;
      background: rgb($color-white, 0.8);
      border: none;
      border-radius: unset;
      box-shadow: unset;
    }
  }

  [class='tooltip-wrapper'] {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    width: $space-500;
    padding: $space-25;
  }

  [class='tooltip-content-x'] {
    @include vertical-margin;
    @include body-3;
  }

  [class='tooltip-content-y'] {
    @include vertical-margin;
    @include body-3-bold;
  }
}
</style>
